interface ISearchHit {
  heading: string;
  url: string;
  excerpt: string | null;
  lastUpdatedDate: string;
  lastUpdatedDateISO: string;
  type: string;
  fileType: string | null;
}

interface ISearchResult {
  searchHits: ISearchHit[];
  totalHits: number;
  filters: string[];
}

interface ITextContent {
  noKeywords: string;
  noResults: string;
  resultFor: string;
  resultsFor: string;
  lastUpdated: string;
  showMore: string;
}

const useSearchPage = (): void => {
  const searchForm = document.querySelector('.search-page__form') as HTMLFormElement;
  const searchInput = document.querySelector('.search-page__input') as HTMLInputElement;
  const searchResultSection = document.querySelector('.search-page__result') as HTMLElement;
  const searchResultInfo = document.querySelector('.search-page__result-info') as HTMLParagraphElement;

  /* Data */
  const { hitsToShow, hitsToAdd, currentLanguage, includeText } = searchForm.dataset;

  /* Text content (translated text) */
  let includeTextArray = includeText?.split(', ') as string[];
  let textContent: ITextContent = {
    noKeywords: includeTextArray[0],
    noResults: includeTextArray[1],
    resultFor: includeTextArray[2],
    resultsFor: includeTextArray[3],
    lastUpdated: includeTextArray[4],
    showMore: includeTextArray[5]
  };

  /* Global variables */
  let query: string | null = null;
  let apiSkipAmount: number = 0;
  let apiTakeAmount: number = parseInt(hitsToShow!, 10);
  let selectedCategoryIds: number[] = [];

  /* Functions – Reset and clear DOM */
  const resetSearchResult = (): void => {
    searchInput.value = '';
    searchResultSection.classList.add('sr-only');
    searchResultInfo.textContent = textContent.noKeywords;
  }

  const clearResultList = (): void => {
    const resultList: HTMLUListElement | null = document.querySelector('.search-page__result-list');
    const showMore: HTMLDivElement | null = document.querySelector('.show-more');

    if (resultList) {
      resultList.remove();
    }

    if (showMore) {
      showMore.remove();
    }
  }

  /* Functions – Get data from API */
  const getSearchResult = async (): Promise<ISearchResult | void> => {
    try {
      let selectedCategoryIdsString = selectedCategoryIds ? selectedCategoryIds.join(',') : '';
      
      const response = await fetch(`/api/search?query=${query}&take=${apiTakeAmount}&skip=${apiSkipAmount}&lang=${currentLanguage}&filter=${selectedCategoryIdsString}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return result;
    } catch (error) {
      console.log(error);
      
      document.getElementById('spinner')!.classList.remove('spinner--show');
    }
  }

  /* Functions – Render DOM */
  const renderResultItems = (searchHits: ISearchHit[], resultList: HTMLUListElement): void => {
    searchHits.forEach(item => {      
      const listItem = document.createElement('li') as HTMLLIElement;
      listItem.classList.add('search-page__result-item', 'content-area-item', 'content-area-item--full', 'site-page-partial');

      if (item.type === 'Media') {
        listItem.classList.add('search-page__result-item--media');
      }
      
      listItem.addEventListener('click', (): void => {
        window.location.href = item.url;
      });
      
      const content = document.createElement('div') as HTMLDivElement;
      content.classList.add('site-page-partial__content');
      listItem.appendChild(content);
      
      const textContainer = document.createElement('div') as HTMLDivElement;
      textContainer.classList.add('site-page-partial__text-container');
      content.appendChild(textContainer);

      const heading = document.createElement('h3') as HTMLHeadingElement;
      heading.classList.add('site-page-partial__heading', 'heading', 'heading--small');
      textContainer.appendChild(heading);

      if (item.type === 'Media') {
        const documentIcon = document.createElement('span') as HTMLSpanElement;
        documentIcon.classList.add('site-page-partial__heading-icon', 'heading__icon', 'icon', 'icon--file');
        heading.classList.add('heading--has-icon');
        heading.appendChild(documentIcon);
      }
      
      const link = document.createElement('a') as HTMLAnchorElement;
      link.classList.add('site-page-partial__link', 'heading__text');
      link.setAttribute('href', item.url);
      link.textContent = item.heading;

      if (item.fileType) {
        link.textContent += ` (${item.fileType})`;
      }
      
      heading.appendChild(link);

      if (item.excerpt) {
        const excerpt = document.createElement('p') as HTMLParagraphElement;
        excerpt.classList.add('site-page-partial__excerpt');
        excerpt.textContent = item.excerpt;
        textContainer.appendChild(excerpt);
      }
      
      const lastUpdatedDate = document.createElement('span') as HTMLSpanElement;
      lastUpdatedDate.classList.add('site-page-partial__date');
      lastUpdatedDate.textContent = `${textContent.lastUpdated}: `
      textContainer.appendChild(lastUpdatedDate);

      const time = document.createElement('time') as HTMLTimeElement;
      time.setAttribute('datetime', item.lastUpdatedDateISO);
      time.textContent = item.lastUpdatedDate;
      lastUpdatedDate.appendChild(time);
      
      const arrowIconContainer = document.createElement('div') as HTMLDivElement;
      arrowIconContainer.classList.add('site-page-partial__arrow-icon-container');
      textContainer.appendChild(arrowIconContainer);
      
      const arrowIcon = document.createElement('span') as HTMLSpanElement;
      if(item.type === 'Media') {
        arrowIcon.classList.add('site-page-partial__arrow-icon', 'icon', 'icon--arrow-down');
      } else {
        arrowIcon.classList.add('site-page-partial__arrow-icon', 'icon', 'icon--arrow-right');        
      }

      arrowIconContainer.appendChild(arrowIcon);

      resultList.appendChild(listItem);
    });
  }

  const renderShowMoreButton = (): void => {
    const showMoreContainer = document.createElement('div');
    showMoreContainer.classList.add('show-more');

    const showMoreButton = document.createElement('button');
    showMoreButton.classList.add('search-page__show-more-button', 'button', 'button--secondary');
    showMoreButton.setAttribute('type', 'button');
    showMoreButton.innerHTML = `<span class="button__icon icon icon--plus"></span> ${textContent.showMore}`;

    showMoreButton.addEventListener('click', (): void => {
      addMoreResultItems(showMoreButton);
    });

    showMoreContainer.appendChild(showMoreButton);
    searchResultSection.appendChild(showMoreContainer);
  }

  const renderResult = (searchResult: ISearchResult): void => {
    clearResultList();

    if (searchResult.totalHits > 0) {      
      const searchResultSectionContainer = searchResultSection.querySelector('.section-block__container') as HTMLDivElement;
      const resultList = document.createElement('ul') as HTMLUListElement;
      resultList.classList.add('search-page__result-list', 'content-area');
      searchResultSectionContainer.appendChild(resultList);

      renderResultItems(searchResult.searchHits, resultList);
      let filterListElement = (document.querySelector('.filter') as HTMLUListElement);
      
      // Render search filters
      if (searchResult.filters && !!filterListElement) {
        filterListElement.innerHTML = Object.entries(searchResult.filters)
          .sort(([_, name1], [__, name2]) => name1.localeCompare(name2, currentLanguage))
          .map(([key, value]) => {
            let isSelected = selectedCategoryIds.includes(parseInt(key));
            return `<li><button class="button button--filter ${isSelected ? 'button--selected' : ''}" data-id="${key}">${value}</button></li>`;
          })
          .join(' ');
        filterListElement.hidden = false;
      } else {
        filterListElement.hidden = true;
        filterListElement.innerHTML = '';
      }

      let clearFilterButton = document.querySelector('.search-page__clear-filter') as HTMLButtonElement;
      
      if (selectedCategoryIds.length > 0){
        clearFilterButton.hidden = false;
      } else {
        clearFilterButton.hidden = true;
      }

      if (searchResult.totalHits === 1) {
        searchResultInfo.innerHTML = `${searchResult.totalHits} ${textContent.resultFor} <strong>${query}</strong>.`;
      } else {
        searchResultInfo.innerHTML = `${searchResult.totalHits} ${textContent.resultsFor} <strong>${query}</strong>.`;
      }
      
      // Add search filter event listeners
      addFilterButtonsEventListeners();

      if (searchResult.searchHits.length < searchResult.totalHits) {
        renderShowMoreButton();
      }
    } else {
      document.getElementById('no-result-text')!.style.display = 'block';
      searchResultInfo.textContent = textContent.noResults;
    }

    searchResultSection.classList.remove('sr-only');
  }

  const renderWithoutResult = (): void => {
    clearResultList();

    let filterListElement: HTMLUListElement | null = (document.querySelector('.filter') as HTMLUListElement);
    let clearFilterButton: HTMLButtonElement = document.querySelector('.search-page__clear-filter') as HTMLButtonElement;
    
    // Remove filters
    if (filterListElement) {
      filterListElement.hidden = true;
      filterListElement.innerHTML = '';
    }
    
    // Hide clear filter button
    clearFilterButton.hidden = true;

    // Reset search result
    searchInput.value = '';
    searchResultInfo.textContent = textContent.noKeywords;
    searchResultSection.classList.remove('sr-only');
  }

  /* Functions - Search */
  const search = async(): Promise<void> => {
    apiSkipAmount = 0;

    let searchContainer = document.querySelector('.search-page__container') as HTMLUListElement;

    searchContainer.style.opacity = '0';
    document.getElementById('spinner')!.classList.add('spinner--show');

    let searchResult = await getSearchResult();

    setTimeout(() => {
      document.getElementById('spinner')!.classList.remove('spinner--show');
      searchContainer.style.opacity = '1';

      if (searchResult) {
        renderResult(searchResult);
      }
    }, 750)
  }

  const searchFromQueryString = (): void => {
    const urlParams = new URLSearchParams(window.location.search) as URLSearchParams;
    let queryFromParam: string | null = urlParams.get('query');
    let takeFromParam: string | null = urlParams.get('take');
    let selectedCategoryIdsFromParam: string | null = urlParams.get('filter');

    query = queryFromParam;

    if (query) {
      query = query.trim();
      searchInput.value = query;
    } else {
      query = '';
    }

    if (takeFromParam && !isNaN(parseInt(takeFromParam, 10))) {
      apiTakeAmount = parseInt(takeFromParam, 10);
    }
    
    if (!!selectedCategoryIdsFromParam?.length) {
      selectedCategoryIds = selectedCategoryIdsFromParam.split(',').map(Number);
    }

    if (query.length > 0) {
      search();
    } else {
      renderWithoutResult();
    }
  }

  const addMoreResultItems = async (showMoreButton: HTMLButtonElement): Promise<void> => {
    apiSkipAmount += apiTakeAmount;
    apiTakeAmount = parseInt(hitsToAdd!, 10);

    let searchResult = await getSearchResult();
    if (searchResult) {
      const resultList = document.querySelector('.search-page__result-list') as HTMLUListElement;
      const resultItemsCount = resultList.children.length as number;
      let takeAmount = resultItemsCount + parseInt(hitsToAdd!, 10);

      if (searchResult.totalHits < takeAmount) {
        takeAmount = searchResult.totalHits;
      }

      let filter = selectedCategoryIds.length > 0 ? `&filter=${selectedCategoryIds.join(',')}` : '';

      window.history.replaceState({},'', `?query=${query}&take=${takeAmount}${filter}`);

      renderResultItems(searchResult.searchHits, resultList);

      if (resultList.children.length === searchResult.totalHits) {
        showMoreButton.parentElement!.remove();
      }

      let resultItems = resultList.children as HTMLCollection;
      let firstAddedResultItem = resultItems[resultItemsCount] as HTMLLIElement;
      let firstAddedResultItemLink = firstAddedResultItem.querySelector('a') as HTMLAnchorElement;
      firstAddedResultItemLink.focus();
    }
  }

  const getSelectedFilterButtons = () => {
    const selectedFilterButtons = document.querySelectorAll('.button--filter.button--selected') as NodeListOf<HTMLButtonElement>;

    // Reset selected category ids
    selectedCategoryIds = [];
    
    // Add selected category ids
    selectedFilterButtons.forEach((button) => {
      selectedCategoryIds.push(parseInt(button.dataset.id!));
    });
    
    return selectedCategoryIds;
  };

  const handleFilterButtonEvent = (event: MouseEvent): void => {
    (event.target as HTMLButtonElement).classList.toggle('button--selected');
    selectedCategoryIds = getSelectedFilterButtons();
    
    // Add filters to query string
    let params = [];
    
    if (!!query) {
      params.push(`query=${query}`);
    }
    
    if (!!apiTakeAmount) {
      params.push(`take=${apiTakeAmount}`);
    }
    
    if (selectedCategoryIds.length > 0) {
      params.push(`filter=${selectedCategoryIds.join(',')}`);
    }
    
    window.history.pushState({},'', `?${params.join('&')}`);
    
    // Clear search result
    clearResultList();

    // Show pages based on selected category id:s
    search();
  };
  
  const addFilterButtonsEventListeners = (): void => {
    const filterButtons = document.querySelectorAll('.button--filter') as NodeListOf<HTMLButtonElement>
    
    if (filterButtons.length > 0) {
      filterButtons.forEach((button) => {
        button.addEventListener('click', handleFilterButtonEvent);
      });
    }
  }

  const initializeEvents = (): void => {
    // Event that happens if someone enters url with query string
    window.addEventListener('load', (): void => {
      if (window.location.search) {
        searchFromQueryString();
      }

      searchInput.focus();
    });

    // Event that happens when users browse through their browser history, e.g. use the back button.
    window.addEventListener('popstate', (): void => {
      if (window.location.search) {
        searchFromQueryString();
      } else {
        resetSearchResult();
      }

      searchInput.focus();
    });

    // Event that happens when user submits a search with the form
    searchForm.addEventListener('submit', (event: SubmitEvent): void => {
      event.preventDefault();

      // If the query is different from the one in the search input, clear the result list
      if(!!query && query.length > 0 && query != searchInput.value.trim()) {
        clearResultList();
      }

      // Clear filters
      selectedCategoryIds = [];

      // Update query string
      query = searchInput.value.trim();
      window.history.pushState({},'', `?query=${query}`);

      if (query.length > 0) {
        search();
      } else {
        renderWithoutResult();
      }
    });

    // Event that happens when user clicks the clear filter button
    (document.querySelector(".search-page__clear-filter") as HTMLButtonElement).addEventListener('click', (event: MouseEvent): void => {
      // Clear query string
      window.history.pushState({},'', `?query=${query}`);
      
      selectedCategoryIds = [];
      apiTakeAmount = parseInt(hitsToShow!, 10);
      apiSkipAmount = 0;
      clearResultList();
      search();
    });
  }
  
  initializeEvents();
};

useSearchPage();
